import { useEffect, useState } from 'react';
import { Form, Input, Select, InputNumber, Switch, Button } from '@/shared/ui/primitives';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import apiClient from '@/lib/apiClient';
import { districtApi } from '../api/district.api';
import { districtKeys } from '../api/district.keys';
import type { District, DistrictFormData } from '@/shared/types';

interface DistrictFormProps {
  initialData?: District;
  onSubmit: (data: DistrictFormData) => void;
  isLoading: boolean;
}

export default function DistrictForm({ initialData, onSubmit, isLoading }: DistrictFormProps) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [cascadeCountryId, setCascadeCountryId] = useState<number>(0);
  const [cascadeRegionId, setCascadeRegionId] = useState<number>(0);

  const { data: countries = [], isLoading: loadingCountries } = useQuery({
    queryKey: districtKeys.countries,
    queryFn: () => districtApi.listCountries(),
  });

  const { data: regions = [], isLoading: loadingRegions } = useQuery({
    queryKey: districtKeys.regionsByCountry(cascadeCountryId),
    queryFn: () => districtApi.listRegionsByCountry(cascadeCountryId),
    enabled: !!cascadeCountryId,
  });

  const { data: cities = [], isLoading: loadingCities } = useQuery({
    queryKey: districtKeys.citiesByRegion(cascadeRegionId),
    queryFn: () => districtApi.listCitiesByRegion(cascadeRegionId),
    enabled: !!cascadeRegionId,
  });

  useEffect(() => {
    if (!initialData) return;

    if (initialData.cityId) {
      apiClient
        .get(`/admin/cities/${initialData.cityId}`)
        .then((res) => {
          const cityData = res.data?.data;
          setCascadeCountryId(cityData?.countryId || 0);
          setCascadeRegionId(cityData?.regionId || 0);
          form.setFieldsValue({
            nameEn: initialData.nameEn || '',
            nameAr: initialData.nameAr || '',
            cityId: initialData.cityId || undefined,
            sortIndex: initialData.sortIndex ?? 0,
            isActive: initialData.isActive ?? true,
          });
        })
        .catch(() => {
          form.setFieldsValue({
            nameEn: initialData.nameEn || '',
            nameAr: initialData.nameAr || '',
            cityId: initialData.cityId || undefined,
            sortIndex: initialData.sortIndex ?? 0,
            isActive: initialData.isActive ?? true,
          });
        });
    } else {
      form.setFieldsValue({
        nameEn: initialData.nameEn || '',
        nameAr: initialData.nameAr || '',
        sortIndex: initialData.sortIndex ?? 0,
        isActive: initialData.isActive ?? true,
      });
    }
  }, [initialData, form]);

  const handleCountryChange = (val: number) => {
    setCascadeCountryId(val);
    setCascadeRegionId(0);
    form.setFieldsValue({ cityId: undefined });
  };

  const handleRegionChange = (val: number) => {
    setCascadeRegionId(val);
    form.setFieldsValue({ cityId: undefined });
  };

  const handleFinish = (values: any) => {
    onSubmit({
      nameEn: values.nameEn,
      nameAr: values.nameAr,
      cityId: values.cityId,
      sortIndex: values.sortIndex,
      isActive: values.isActive,
    });
  };

  return (
    <Form form={form} layout="vertical" onFinish={handleFinish} initialValues={{ isActive: true, sortIndex: 0 }} style={{ maxWidth: 480 }}>
      <Form.Item name="nameEn" label={t('districts.nameEn')} rules={[{ required: true }]}>
        <Input placeholder={t('districts.nameEnPlaceholder')} />
      </Form.Item>
      <Form.Item name="nameAr" label={t('districts.nameAr')} rules={[{ required: true }]}>
        <Input dir="rtl" placeholder={t('districts.nameArPlaceholder')} />
      </Form.Item>
      <Form.Item label={t('districts.country')} required>
        <Select
          placeholder={t('districts.selectCountry')}
          loading={loadingCountries}
          value={cascadeCountryId || undefined}
          onChange={handleCountryChange}
          options={countries.map((c) => ({ value: c.id, label: c.nameEn }))}
        />
      </Form.Item>
      <Form.Item label={t('districts.region')} required>
        <Select
          placeholder={!cascadeCountryId ? t('districts.selectCountryFirst') : t('districts.selectRegion')}
          loading={loadingRegions}
          disabled={!cascadeCountryId}
          value={cascadeRegionId || undefined}
          onChange={handleRegionChange}
          options={regions.map((r) => ({ value: r.id, label: r.nameEn }))}
        />
      </Form.Item>
      <Form.Item name="cityId" label={t('districts.city')} rules={[{ required: true }]}>
        <Select
          placeholder={!cascadeRegionId ? t('districts.selectRegionFirst') : t('districts.selectCity')}
          loading={loadingCities}
          disabled={!cascadeRegionId}
          options={cities.map((c) => ({ value: c.id, label: c.nameEn }))}
        />
      </Form.Item>
      <Form.Item name="sortIndex" label={t('districts.sortIndex')} rules={[{ required: true }]}>
        <InputNumber style={{ width: '100%' }} />
      </Form.Item>
      <Form.Item name="isActive" label={t('common.active')} valuePropName="checked">
        <Switch />
      </Form.Item>
      <Form.Item>
        <Button type="primary" htmlType="submit" loading={isLoading}>{t('common.save')}</Button>
      </Form.Item>
    </Form>
  );
}
